import React, { useState } from "react";
import { Button, Modal } from "../components/ui";
import { wfAspectOverlap } from "../helpers/wfImageAutoFix.js";

/**
 * Pad-or-crop chooser for a fixed/square WatchFinder slot. Both previews are
 * the ORIGINAL image under CSS object-fit (contain = pad, cover = crop) in a
 * frame of the target's aspect ratio. object-position's percentage maths is
 * the same as wfCoverRect's `pos`, so what's shown is what gets uploaded.
 * Phones: Modal's "auto" variant is a bottom sheet; the two previews stay side
 * by side (comparison is the point) and the slider is full width.
 */
export default function WfFixChoiceModal({ label, src, spec, dims, onClose, onChoose }) {
  const [pos, setPos] = useState(50);
  const overlap = dims ? Math.round(wfAspectOverlap(dims.w, dims.h, spec.w, spec.h) * 100) : null;
  // which way the crop overflows: a photo wider than the frame loses width
  const cropsWidth = !dims || dims.w / dims.h > spec.w / spec.h;
  const frameMod = spec.kind === "square" ? "square" : "portrait";

  return (
    <Modal
      open
      onClose={onClose}
      size="lg"
      title={`Auto-fix ${label}`}
      description={`WatchFinder needs ${spec.w}×${spec.h}px. Choose how to fit this photo.`}
      className="wf-fix-choice"
    >
      <div className="wf-fix-choice__options">
        <div className="wf-fix-choice__option">
          <div className={`wf-fix-choice__frame wf-fix-choice__frame--${frameMod}`}>
            <img src={src} alt={`${label} padded with white`} className="wf-fix-choice__img wf-fix-choice__img--pad" />
          </div>
          <div className="wf-fix-choice__name">Keep whole photo</div>
          <div className="wf-fix-choice__note">
            Nothing is cut off. {overlap !== null && overlap < 100 ? `Photo fills ${overlap}% of the frame; the rest is white.` : "Gaps are filled white."}
          </div>
          <Button variant="secondary" fullWidth data-btn="wf-fix-pad" onClick={() => onChoose("pad")}>
            Use white padding
          </Button>
        </div>

        <div className="wf-fix-choice__option">
          <div className={`wf-fix-choice__frame wf-fix-choice__frame--${frameMod}`}>
            <img
              src={src}
              alt={`${label} cropped to fill`}
              className="wf-fix-choice__img wf-fix-choice__img--crop"
              style={{ objectPosition: `${pos}% ${pos}%` }}
            />
          </div>
          <div className="wf-fix-choice__name">Crop to fill</div>
          <div className="wf-fix-choice__note">
            No white space. {overlap !== null && overlap < 100 ? `Keeps ${overlap}% of the photo's ${cropsWidth ? "width" : "height"}.` : ""}
          </div>
          {/* slider gets the full card width; end labels sit underneath (phones) */}
          <div className="wf-fix-choice__slider">
            <input
              type="range"
              min="0"
              max="100"
              value={pos}
              aria-label={`Crop position, ${cropsWidth ? "left to right" : "top to bottom"}`}
              onChange={(e) => setPos(Number(e.target.value))}
            />
            <div className="wf-fix-choice__slider-ends" aria-hidden="true">
              <span>{cropsWidth ? "Left" : "Top"}</span>
              <span>{cropsWidth ? "Right" : "Bottom"}</span>
            </div>
          </div>
          <Button variant="primary" fullWidth data-btn="wf-fix-crop" onClick={() => onChoose("crop", pos / 100)}>
            Use crop
          </Button>
        </div>
      </div>
    </Modal>
  );
}
